import { useState, useMemo } from "react"
import { Dialog, DialogContent, DialogTrigger } from "../../../../components/ui/dialog"
import { useWidget } from "../../../../providers/widget"
import { useExplorer } from "../../providers"
import { useExplorerCss } from "../../hooks/useExplorerCss"
import { useFetchExplorerMeta, curateOptions, priceFieldForMode } from "../../data"
import { ExploreWidgetConfig } from "../../config"

type ZoneOption = { id: number | string; name: string; description?: string }

/**
 * "zone" Where mode (WP: primary_field_where_source = zone): a combined picker with
 * two panels — Our Regions (locations) and All Cities — each curated + ordered by the
 * matching filterOptions dimension. Writes both the `locations` and `cities` filters.
 * Sub-toggles: zoneMultiselect, hideZoneSearch, hideZoneGroups.
 */
export default function ZoneFilter() {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const cssString = useExplorerCss()
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState("")

  const multiselect = options.zoneMultiselect === true
  const hideSearch = options.hideZoneSearch === true
  const hideGroups = options.hideZoneGroups === true

  const [selectedRegions, setSelectedRegions] = useState<string[]>(
    state.visitorFilters.locations?.split(',').filter(Boolean) ?? []
  )
  const [selectedCities, setSelectedCities] = useState<string[]>(
    state.visitorFilters.cities?.split(',').filter(Boolean) ?? []
  )

  // priceField isn't used here, but passing it keeps the query key identical to
  // AdditionalFilters and the SSR prefetch — one meta entry, no duplicate fetch.
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceFieldForMode(options.priceDisplay.noDates))
  const meta = metaData?.result

  const regionOptions = useMemo<ZoneOption[]>(() =>
    curateOptions(meta?.taxonomies?.locations ?? [], options.filterOptions?.locations?.split(',').filter(Boolean)),
    [meta?.taxonomies?.locations, options.filterOptions])

  const cityOptions = useMemo<ZoneOption[]>(() => {
    const cities = meta?.location?.cities?.length ? meta.location.cities : (meta?.taxonomies?.cities ?? [])
    return curateOptions(cities, options.filterOptions?.cities?.split(',').filter(Boolean))
  }, [meta?.location?.cities, meta?.taxonomies?.cities, options.filterOptions])

  const filterByKeyword = (list: ZoneOption[]) =>
    keyword ? list.filter(o => o.name.toLowerCase().includes(keyword.toLowerCase())) : list

  const displayValue = useMemo(() => {
    const names: string[] = []
    for (const r of regionOptions) if (selectedRegions.includes(String(r.id))) names.push(r.name)
    for (const c of cityOptions) if (selectedCities.includes(String(c.id))) names.push(c.name)
    return names.join(', ')
  }, [selectedRegions, selectedCities, regionOptions, cityOptions])

  const toggle = (kind: 'region' | 'city', id: string) => {
    if (!multiselect) {
      // single selection across both panels
      if (kind === 'region') {
        setSelectedRegions(prev => (prev.includes(id) ? [] : [id]))
        setSelectedCities([])
      } else {
        setSelectedCities(prev => (prev.includes(id) ? [] : [id]))
        setSelectedRegions([])
      }
      return
    }
    const setter = kind === 'region' ? setSelectedRegions : setSelectedCities
    setter(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const handleApply = () => {
    setFilter({
      locations: selectedRegions.length ? selectedRegions.join(',') : undefined,
      cities: selectedCities.length ? selectedCities.join(',') : undefined,
    })
    setOpen(false)
  }

  const handleClear = () => {
    setSelectedRegions([])
    setSelectedCities([])
    setFilter({ locations: undefined, cities: undefined })
  }

  const hasSelection = selectedRegions.length > 0 || selectedCities.length > 0

  const renderPanel = (kind: 'region' | 'city', label: string, list: ZoneOption[], selected: string[]) => (
    <>
      {!hideGroups && <div className="hfse-form-zone-label hfse-form-zone-label-cities">{label}</div>}
      <div className="hfse-form-zone-subfield hfse-form-zone-subfield-cities">
        {filterByKeyword(list).map(opt => (
          <button
            key={`${kind}-${opt.id}`}
            type="button"
            className={`hfse-form-zone-btn hfse-form-zone-city ${selected.includes(String(opt.id)) ? 'hfse-selected' : ''}`}
            data-value={opt.id}
            onClick={() => toggle(kind, String(opt.id))}
          >
            <div className="name">{opt.name}</div>
            {opt.description && <div className="subheading">{opt.description}</div>}
          </button>
        ))}
        {filterByKeyword(list).length === 0 && (
          <div className="hfse-form-zone-no-results">No items found.</div>
        )}
      </div>
    </>
  )

  return (
    <div className="hfse-form-zone-field-container">
      <div className="field-control hfse-form-zone-field-control">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="hfse-form-field-trigger">
              <span className="hfse-form-field-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <input
                type="text"
                className="hk-input hfse-form-zone-options-trigger"
                placeholder="Anywhere"
                value={displayValue}
                readOnly
              />
            </div>
          </DialogTrigger>
          <DialogContent className="hfse-form-modal hfse-form-zone-options" elevateDom cssString={cssString} title="Where" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="hk-modal-header">
              <span className="hk-h4" style={{ paddingBottom: '10px' }}>
                Where
              </span>
            </div>

            <div className="hk-modal-body hk-flex-1">
              {!hideSearch && (
                <div className="hk-position-relative hk-margin-bottom hfse-form-zone-input">
                  <span className="hfse-form-zone-input-fields">
                    <span className="hfse-form-zone-keyword-container">
                      <input
                        type="text"
                        className="hfse-form-zone-keyword"
                        placeholder="Search…"
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                      />
                    </span>
                  </span>
                </div>
              )}

              {regionOptions.length > 0 && renderPanel('region', 'Our Regions', regionOptions, selectedRegions)}
              {renderPanel('city', 'All Cities', cityOptions, selectedCities)}
            </div>

            <div className="hk-modal-footer hk-flex">
              {hasSelection && (
                <button type="button" className="hk-button hk-button-link hfse-modal-reset-button" onClick={handleClear}>
                  Clear Filters
                </button>
              )}
              <button
                type="button"
                className="hk-button hk-button-small bg-accent text-accent-foreground hk-margin-auto-left hfse-modal-next-button"
                onClick={handleApply}
                style={{ marginLeft: 'auto' }}
              >
                Apply
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
